import {
  ELEC_CAP_COM_2015,
  ELEC_CAP_IND_2015,
  ELEC_CAP_NONRES_2015,
  ELEC_CAP_NONRES_2019,
  ELEC_CAP_RES_2015,
  ELEC_CAP_RES_2019,
  ELEC_CAP_TOTAL_2015,
  ELEC_CAP_TOTAL_2019,
  BH_EXPERIMENT_GEOID,
  GAS_CAP_TOTAL_2015,
  GAS_CAP_TOTAL_2019,
  GAS_RES_MODEL_2015,
  GAS_RES_MODEL_2019,
} from './bhSourceControls';
import {
  buildGasResidentialCommercialByYear,
  GAS_ASSUMPTION_UNREPORTED_NEGLIGIBLE,
  type GasComponentProvenance,
} from './gasResidentialCommercialSeries';
import type { ActivityEstimate, CanonicalEnergyActivityYear, CoverageStatus, ExperimentFuel } from './types';
import type { BhExperimentLegacyContext } from './legacyContext';
import { legacyValue } from './legacyContext';
import { buildPiecewiseResidentialOrSectorSeries, experimentYears } from './piecewiseActivity';
import {
  BH_SCE_ELECTRICITY_OBSERVED,
  sceNonresidentialTotal,
  sceObservedByYear,
} from './sceSoCalObservedComponents';

function est(
  partial: Omit<ActivityEstimate, 'includedCategories' | 'missingCategories'> & {
    includedCategories?: string[];
    missingCategories?: string[];
  }
): ActivityEstimate {
  return {
    includedCategories: partial.includedCategories ?? [],
    missingCategories: partial.missingCategories ?? [],
    ...partial,
  };
}

function coverageForElecYear(
  year: number,
  observed: boolean
): CoverageStatus {
  if (observed) return 'complete_observed';
  if (year === 2015) return 'cap_published';
  if (year === 2019) return 'cap_derived_total';
  if (year === 2020) return 'interpolated';
  if (year < 2015) return 'extrapolated';
  if (year > 2015 && year < 2019) return 'interpolated';
  return 'extrapolated';
}

export function buildBhElectricityCanonicalSeries(
  legacy: BhExperimentLegacyContext
): CanonicalEnergyActivityYear[] {
  const resKnots = new Map<number, number>([
    [2015, ELEC_CAP_RES_2015],
    [2019, ELEC_CAP_RES_2019],
    ...BH_SCE_ELECTRICITY_OBSERVED.map((r) => [r.year, r.residential] as [number, number]),
  ]);
  const nonresKnots = new Map<number, number>([
    [2015, ELEC_CAP_NONRES_2015],
    [2019, ELEC_CAP_NONRES_2019],
    ...BH_SCE_ELECTRICITY_OBSERVED.map((r) => [r.year, sceNonresidentialTotal(r)] as [number, number]),
  ]);

  const resSeries = buildPiecewiseResidentialOrSectorSeries(legacy.physicalElecRes, resKnots);
  const nonresSeries = buildPiecewiseResidentialOrSectorSeries(legacy.physicalElecCom, nonresKnots);

  return experimentYears().map((year) => {
    const sce = sceObservedByYear(year);
    const resVal = resSeries.get(year)!;
    const nonresVal = nonresSeries.get(year)!;
    const observed = sce != null;

    const residential = est({
      value: resVal,
      unit: 'kWh',
      coverage: coverageForElecYear(year, observed && year >= 2021),
      sourceClass: year === 2015 ? 'cap_published' : year === 2019 ? 'cap_derived' : observed ? 'utility_observed' : 'hybrid',
      methodId: observed ? 'sce_residential_observed' : 'bh_elec_res_piecewise_v2',
      sourceNote:
        year === 2019
          ? 'CAP-derived allocation from electricity-emissions shares; assumes common effective factor.'
          : observed
            ? 'SCE aggregated annual usage — residential'
            : 'Model-shaped series anchored to CAP / SCE knots',
      includedCategories: ['residential'],
      missingCategories: [],
    });

    const commercial = est({
      value: sce?.commercial ?? (year === 2015 ? ELEC_CAP_COM_2015 : null),
      unit: 'kWh',
      coverage: sce ? 'complete_observed' : year === 2015 ? 'cap_published' : 'extrapolated',
      sourceClass: sce ? 'utility_observed' : year === 2015 ? 'cap_published' : 'modeled',
      methodId: 'sce_commercial_component',
      sourceNote: sce ? 'SCE commercial Total kwh' : year === 2015 ? 'CAP Appendix C commercial' : '',
      includedCategories: sce || year === 2015 ? ['commercial'] : [],
      missingCategories: sce || year === 2015 ? [] : ['commercial'],
      commercialComponent: sce?.commercial ?? (year === 2015 ? ELEC_CAP_COM_2015 : null),
    });

    const industrial = est({
      value: sce?.industrial ?? (year === 2015 ? ELEC_CAP_IND_2015 : null),
      unit: 'kWh',
      coverage: sce?.industrial != null ? 'complete_observed' : year === 2015 ? 'cap_published' : 'extrapolated',
      sourceClass: sce?.industrial != null ? 'utility_observed' : year === 2015 ? 'cap_published' : 'modeled',
      methodId: 'sce_industrial_component',
      sourceNote: sce?.industrial != null ? 'SCE industrial Total kwh' : year === 2015 ? 'CAP Appendix C industrial' : '',
      includedCategories: sce?.industrial != null || year === 2015 ? ['industrial'] : [],
      missingCategories: sce?.industrial != null || year === 2015 ? [] : ['industrial'],
      industrialComponent: sce?.industrial ?? (year === 2015 ? ELEC_CAP_IND_2015 : null),
    });

    const nonresidential = est({
      value: nonresVal,
      unit: 'kWh',
      coverage: coverageForElecYear(year, observed),
      sourceClass: year === 2015 ? 'cap_published' : year === 2019 ? 'cap_derived' : observed ? 'utility_observed' : 'hybrid',
      methodId: 'bh_elec_nonres_complete_v2',
      sourceNote: 'Nonresidential = commercial + industrial + other applicable categories (never commercial-only).',
      includedCategories: ['commercial', 'industrial'],
      missingCategories: ['agricultural'],
      commercialComponent:
        year === 2015 || (observed && year >= 2021) ? commercial.value : null,
      industrialComponent:
        year === 2015 || (observed && year >= 2021 && industrial.value != null)
          ? industrial.value
          : null,
    });

    const totalVal = resVal + nonresVal;
    const total = est({
      value: totalVal,
      unit: 'kWh',
      coverage: residential.coverage,
      sourceClass: residential.sourceClass,
      methodId: 'residential_plus_nonresidential',
      sourceNote: 'Sum of experimental residential and complete nonresidential.',
      includedCategories: ['residential', 'nonresidential'],
      missingCategories: [],
    });

    const reconciliation =
      year === 2015 || year === 2019
        ? {
            residentialPlusNonresidential: totalVal,
            controlTotal: year === 2015 ? ELEC_CAP_TOTAL_2015 : ELEC_CAP_TOTAL_2019,
            delta: totalVal - (year === 2015 ? ELEC_CAP_TOTAL_2015 : ELEC_CAP_TOTAL_2019),
            passes: Math.abs(totalVal - (year === 2015 ? ELEC_CAP_TOTAL_2015 : ELEC_CAP_TOTAL_2019)) < 1,
            note: 'CAP electricity total control',
          }
        : undefined;

    return {
      geoid: BH_EXPERIMENT_GEOID,
      year,
      fuel: 'electricity' as ExperimentFuel,
      residential,
      commercial,
      industrial,
      agricultural: est({
        value: null,
        unit: 'kWh',
        coverage: 'extrapolated',
        sourceClass: 'modeled',
        methodId: 'sce_agricultural_blank',
        sourceNote: 'SCE agricultural blank — treated as null, not zero.',
        includedCategories: [],
        missingCategories: ['agricultural'],
      }),
      otherNonresidential: est({
        value: null,
        unit: 'kWh',
        coverage: 'extrapolated',
        sourceClass: 'modeled',
        methodId: 'other_nonresidential_none',
        sourceNote: '',
        includedCategories: [],
        missingCategories: ['other_nonresidential'],
      }),
      nonresidential,
      total,
      reconciliation,
      legacyPhysicalResidential: legacyValue(legacy.physicalElecRes, year),
      legacyPhysicalCommercial: legacyValue(legacy.physicalElecCom, year),
      legacyGhgResidential: legacyValue(legacy.ghgElecRes, year),
      legacyGhgCommercial: legacyValue(legacy.ghgElecCom, year),
    };
  });
}

function gasCoverageFromProvenance(p: GasComponentProvenance): CoverageStatus {
  switch (p) {
    case 'utility_reported':
      return 'complete_observed';
    case 'cap_calibrated_estimate':
      return 'cap_derived_total';
    case 'model_backcast':
      return 'extrapolated';
    case 'model_interpolated':
      return 'interpolated';
    default:
      return 'interpolated';
  }
}

export function buildBhGasCanonicalSeries(legacy: BhExperimentLegacyContext): CanonicalEnergyActivityYear[] {
  const gasByYear = buildGasResidentialCommercialByYear(legacy);

  return Array.from(gasByYear.values())
    .sort((a, b) => a.year - b.year)
    .map((g) => {
      const { year, residential: resVal, commercialCategory: comVal, total: totalVal } = g;
      if (!Number.isFinite(resVal) || !Number.isFinite(comVal) || resVal < 0 || comVal < 0) {
        throw new Error(`Invalid gas components at ${year}`);
      }
      if (Math.abs(totalVal - (resVal + comVal)) > 1e-6) {
        throw new Error(`Gas total identity failed at ${year}`);
      }

      const residential = est({
        value: resVal,
        unit: 'therms',
        coverage: gasCoverageFromProvenance(g.residentialProvenance),
        sourceClass: g.residentialProvenance === 'utility_reported' ? 'utility_observed' : 'modeled',
        methodId: `bh_gas_res_${g.residentialProvenance}`,
        sourceNote:
          g.residentialProvenance === 'utility_reported'
            ? 'SoCalGas reported residential therms.'
            : g.residentialProvenance === 'cap_calibrated_estimate'
              ? 'Residential estimate at CAP anchor; CAP does not publish a gas-sector therm split.'
              : 'Residential from ClimatePlans physical model shape with CAP/utility calibration.',
        includedCategories: ['residential'],
        missingCategories: [],
      });

      const commercial = est({
        value: comVal,
        unit: 'therms',
        coverage: gasCoverageFromProvenance(g.commercialProvenance),
        sourceClass:
          g.commercialProvenance === 'utility_reported'
            ? 'utility_observed'
            : g.commercialProvenance === 'cap_calibrated_estimate'
              ? 'cap_derived'
              : 'modeled',
        methodId: `bh_gas_commercial_category_${g.commercialProvenance}`,
        sourceNote:
          g.commercialProvenance === 'utility_reported'
            ? 'SoCalGas reported commercial-category therms (industrial may be absent or included — unresolved).'
            : g.commercialProvenance === 'cap_calibrated_estimate'
              ? 'Commercial-category residual = CAP-derived total − residential estimate at anchor year.'
              : 'Commercial category from calibrated physical model shape.',
        includedCategories: ['commercial_category'],
        missingCategories: ['industrial'],
        commercialComponent: comVal,
      });

      const industrial = est({
        value: null,
        unit: 'therms',
        coverage: 'extrapolated',
        sourceClass: 'modeled',
        methodId: 'gas_industrial_not_estimated',
        sourceNote: 'Industrial not estimated; not stored as zero.',
        includedCategories: [],
        missingCategories: ['industrial'],
        industrialComponent: null,
      });

      const nonresidential = est({
        value: comVal,
        unit: 'therms',
        coverage: gasCoverageFromProvenance(g.commercialProvenance),
        sourceClass: commercial.sourceClass,
        methodId: 'bh_gas_commercial_category_nonres_proxy',
        sourceNote:
          'Nonresidential chart displays commercial-category gas under the negligible-unreported-consumption assumption.',
        includedCategories: ['commercial_category'],
        missingCategories: ['industrial'],
        commercialComponent: comVal,
      });

      const total = est({
        value: totalVal,
        unit: 'therms',
        coverage: residential.coverage,
        sourceClass: 'hybrid',
        methodId: 'residential_plus_commercial_category',
        sourceNote: 'Residential + commercial-category total under negligible-unreported-consumption assumption.',
        includedCategories: ['residential', 'commercial_category'],
        missingCategories: [],
      });

      const reconciliation =
        year === 2015 || year === 2019
          ? {
              residentialPlusNonresidential: totalVal,
              controlTotal: year === 2015 ? GAS_CAP_TOTAL_2015 : GAS_CAP_TOTAL_2019,
              delta: totalVal - (year === 2015 ? GAS_CAP_TOTAL_2015 : GAS_CAP_TOTAL_2019),
              passes: Math.abs(totalVal - (year === 2015 ? GAS_CAP_TOTAL_2015 : GAS_CAP_TOTAL_2019)) < 0.01,
              note: 'CAP-derived gas total (emissions ÷ factor); res + commercial-category',
            }
          : g.residentialProvenance === 'utility_reported'
            ? {
                residentialPlusNonresidential: totalVal,
                controlTotal: totalVal,
                delta: 0,
                passes: true,
                note: 'SoCalGas residential + commercial-category sum',
              }
            : undefined;

      return {
        geoid: BH_EXPERIMENT_GEOID,
        year,
        fuel: 'natural_gas',
        residential,
        commercial,
        industrial,
        agricultural: est({
          value: null,
          unit: 'therms',
          coverage: 'extrapolated',
          sourceClass: 'modeled',
          methodId: 'n/a',
          sourceNote: '',
          includedCategories: [],
          missingCategories: [],
        }),
        otherNonresidential: est({
          value: null,
          unit: 'therms',
          coverage: 'extrapolated',
          sourceClass: 'modeled',
          methodId: 'n/a',
          sourceNote: '',
          includedCategories: [],
          missingCategories: [],
        }),
        nonresidential,
        total,
        reconciliation,
        legacyPhysicalResidential: legacyValue(legacy.physicalGasRes, year),
        legacyPhysicalCommercial: legacyValue(legacy.physicalGasCom, year),
        legacyGhgResidential: legacyValue(legacy.ghgGasRes, year),
        legacyGhgCommercial: legacyValue(legacy.ghgGasCom, year),
        gasAssumptionId: GAS_ASSUMPTION_UNREPORTED_NEGLIGIBLE,
      };
    });
}
